import { Controller } from '@hotwired/stimulus'
import Dropdown, { type DropdownOptions } from '@/lib/dropdown'

export default class extends Controller<HTMLElement> {
  static values = {
    config: { type: Object, default: {} }
  }

  declare readonly configValue: DropdownOptions
  declare dropdown: Dropdown
  declare skipBlurCommit?: boolean
  declare searchEdited?: boolean

  connect() {
    // Building the wrapper moves a <select> into it, which disconnects and reconnects this
    // controller: the second connect finds the dropdown already set up.
    if (Dropdown.for(this.element)) return

    if (!this.element.classList.contains('selection')) {
      this.element.classList.add('dropdown--from-button')
    }

    this.dropdown = new Dropdown(this.element, {
      ...this.config(),
      onShow: () => {
        this.dispatch('show')
        setTimeout(() => this.adjustDirection(), 0)
      }
    })
    if (!this.dropdown.menu) return

    // A disabled <select> is mirrored with a class on the wrapper, but the search input
    // generated next to it stays focusable: without this, a disabled dropdown is still
    // reachable with the keyboard.
    if ((this.element as HTMLSelectElement).disabled && this.dropdown.search) this.dropdown.search.disabled = true

    if (this.dropdown.module.classList.contains('allow-additions')) {
      if (this.dropdown.isMultiple) {
        this.setupMultipleAllowAdditions()
      } else {
        this.setupComboSelect()
      }
    }
  }

  get search(): HTMLInputElement {
    return this.dropdown.search!
  }

  // Multiple `allow-additions` commits an explicit non-empty search term on blur, never the
  // highlighted item: a forced selection would add a contact by accident.
  setupMultipleAllowAdditions() {
    this.dropdown.setting('forceSelection', false)

    this.dropdown.menu.addEventListener('mousedown', () => {
      this.skipBlurCommit = true
    })
    this.search.addEventListener('blur', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
        return
      }

      const value = this.search.value.trim()
      if (value) this.dropdown.setSelected(value)
    })
  }

  // Single `allow-additions` dropdowns behave as a combobox: the search input always shows
  // the committed value, and whatever it contains on blur or Enter becomes the selection.
  // An emptied input clears it only when the <select> offers a blank option; otherwise the
  // current value is restored.
  setupComboSelect() {
    this.dropdown.setting('forceSelection', false)
    this.dropdown.setting('keepSearchTerm', true)

    // Only the select: the search input fires its own native change on blur, which must not
    // resync the old selection.
    this.dropdown.input!.addEventListener('change', () => this.syncSearchInput())
    this.syncSearchInput()

    // Enter commits the suggestion reached with the arrows, or else the typed text.
    this.search.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return

        event.preventDefault()
        event.stopImmediatePropagation()
        const choice = this.dropdown.keyboardChoice()
        if (choice) this.search.value = this.dropdown.itemValue(choice)
        this.commitSearchValue()
        this.dropdown.hide()
      },
      { capture: true }
    )
    this.search.addEventListener('click', () => this.search.select())
    // Only user edits fire `input`: an emptied input clears the selection right away. Without
    // a blank option there is nothing to commit: refilling the search input here would fight
    // the user who is clearing it to retype.
    this.search.addEventListener('input', () => {
      this.searchEdited = true
      if (!this.search.value && this.allowsBlank()) this.commitSearchValue()
    })
    // A menu click blurs the search input first: let the dropdown handle it alone.
    this.dropdown.menu.addEventListener('mousedown', () => {
      this.skipBlurCommit = true
    })
    // Picking the current value again changes nothing, yet the search shows it again.
    this.dropdown.menu.addEventListener('click', () => this.syncSearchInput())
    this.search.addEventListener('focus', () => {
      this.skipBlurCommit = false
      this.searchEdited = false
    })
    this.search.addEventListener('blur', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
      } else if (this.search.value || this.searchEdited) {
        this.commitSearchValue()
      } else {
        this.syncSearchInput()
      }
    })
  }

  commitSearchValue() {
    let value = this.search.value.trim()
    // A `numeric-additions` combo-select stores its values with a dot but displays them the
    // French way (« 5,5% »): the typed text has to be normalized back to the stored format,
    // otherwise it matches no option and is committed as an addition instead.
    if (this.dropdown.module.classList.contains('numeric-additions')) {
      value = value.replace(/\s*%$/, '').replace(',', '.')
    }

    if (value !== this.selectedValue()) {
      if (value) {
        this.dropdown.setSelected(value)
      } else if (this.allowsBlank()) {
        this.dropdown.clear()
      }
    }

    this.syncSearchInput()
  }

  // A combo-select rendered with `include_blank: false` has no empty value the user could
  // reach through the menu: emptying the search input must not clear the <select> either
  // (a cleared VAT rate would silently persist as 0%), it falls back to the committed value.
  allowsBlank() {
    return !!this.dropdown.module.querySelector('select option[value=""]')
  }

  syncSearchInput() {
    const value = this.selectedValue()
    this.search.value = this.displayValue(value)
    this.dropdown.text?.classList.toggle('filtered', !!value)
  }

  // The search input is what the user reads once a value is committed: a `numeric-additions`
  // rate stored as « 5.5 » has to show up as « 5,5% », like the option labels. Non numeric
  // forced values stay as they are.
  displayValue(value: string) {
    if (!value || !this.dropdown.module.classList.contains('numeric-additions')) return value
    if (!/^\d+(\.\d+)?$/.test(value)) return value

    return `${value.replace('.', ',')}%`
  }

  selectedValue() {
    return this.dropdown.values()[0] || ''
  }

  adjustDirection() {
    const { module, menu } = this.dropdown
    if ('dropdownNoUpward' in module.dataset || 'dropdownNoUpward' in this.element.dataset) return
    if (this.configValue.direction === 'upward' || !menu.offsetHeight) return

    const rect = module.getBoundingClientRect()
    const bounds = this.clippingBounds(module)
    const spaceBelow = bounds.bottom - rect.bottom
    const spaceAbove = rect.top - bounds.top

    module.classList.toggle('upward', spaceBelow < menu.offsetHeight && spaceAbove > menu.offsetHeight)
  }

  // A scrolling ancestor — a modal body — clips the menu long before the viewport does, so
  // the room that decides the direction is the one left inside it.
  clippingBounds(element: HTMLElement) {
    let top = 0
    let bottom = window.innerHeight

    // <body> stops the walk: its own overflow is propagated to the viewport, so its box is
    // not a clipping edge — on a scrolled page it sits above the fold.
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      if (!/(auto|scroll|hidden)/.test(getComputedStyle(node).overflowY)) continue

      const rect = node.getBoundingClientRect()
      top = Math.max(top, rect.top)
      bottom = Math.min(bottom, rect.bottom)
    }

    return { top, bottom }
  }

  config(): DropdownOptions {
    return {
      ...(this.element.classList.contains('dropdown-with-blank') ? { placeholder: false } : {}),
      ...(this.element.classList.contains('allow-additions')
        ? {
            allowAdditions: true,
            hideAdditions: !((this.element as HTMLSelectElement).multiple || this.element.classList.contains('multiple'))
          }
        : {}),
      ...this.configValue
    }
  }
}
